import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Download, Smartphone, Share, Plus, ChevronRight, X } from "lucide-react";

// Detect platform
const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isAndroid = () => /Android/.test(navigator.userAgent);
const isMobile = () => isIOS() || isAndroid();
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const TOAST_KEY = "pwa_toast_shown_v1";
const DISMISS_KEY = "pwa_dismissed";

export default function PWAInstallGuide() {
  const [open, setOpen] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [platform, setPlatform] = useState("desktop");
  const [hidden, setHidden] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastLeaving, setToastLeaving] = useState(false);

  useEffect(() => {
    if (isStandalone()) { setHidden(true); return; }
    if (localStorage.getItem(DISMISS_KEY) === "1") setHidden(true);
    setPlatform(isIOS() ? "ios" : isAndroid() ? "android" : "desktop");
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  // Auto-toast on first mobile visit
  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY) === "1") return;
    if (localStorage.getItem(TOAST_KEY) === "1") return;
    if (!isMobile()) return;
    const t = setTimeout(() => {
      setToastOpen(true);
      localStorage.setItem(TOAST_KEY, "1");
    }, 4000);
    return () => clearTimeout(t);
  }, []);

  const closeToast = () => {
    setToastLeaving(true);
    setTimeout(() => { setToastOpen(false); setToastLeaving(false); }, 250);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setHidden(true);
      setDeferred(null);
      setOpen(false);
      closeToast();
    } else { setOpen(true); closeToast(); }
  };

  const dismiss = () => { localStorage.setItem(DISMISS_KEY,"1"); setHidden(true); setOpen(false); closeToast(); };

  if (hidden) return null;

  return (
    <>
      {/* First-visit friendly install toast (mobile only) */}
      {toastOpen && (
        <div
          data-testid="pwa-install-toast"
          className={`fixed left-3 right-3 bottom-3 z-50 sm:left-auto sm:right-5 sm:bottom-5 sm:max-w-sm rounded-2xl border border-[#ea580c]/30 bg-white shadow-2xl overflow-hidden ${toastLeaving ? "animate-toast-out" : "animate-toast-in"}`}
          role="dialog" aria-live="polite"
        >
          <div className="h-1 bg-gradient-to-r from-[#ea580c] via-amber-400 to-[#ea580c]" />
          <div className="p-4 flex gap-3">
            <div className="w-11 h-11 rounded-xl overflow-hidden bg-white border border-slate-200 shadow flex-shrink-0">
              <img src="/apple-touch-icon.png" alt="Shruti T&T" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-[15px] text-[#0f1c56] leading-tight">Add Shruti T&amp;T to your home screen</div>
              <div className="text-[12px] text-slate-600 mt-1 leading-snug">One-tap booking, works offline, and feels like a real app.</div>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={install}
                  data-testid="pwa-toast-install"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#ea580c] hover:bg-orange-700 text-white text-xs font-bold shadow"
                >
                  <Download className="w-3.5 h-3.5" /> {deferred ? "Install" : "Show me how"}
                </button>
                <button
                  onClick={closeToast}
                  data-testid="pwa-toast-later"
                  className="inline-flex items-center px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold"
                >
                  Maybe later
                </button>
              </div>
            </div>
            <button
              onClick={closeToast}
              data-testid="pwa-toast-close"
              aria-label="Dismiss"
              className="text-slate-400 hover:text-slate-600 self-start"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <button onClick={() => setOpen(true)}
        data-testid="pwa-install-btn"
        className="fixed bottom-5 right-5 z-40 bg-[#ea580c] hover:bg-orange-700 text-white pl-4 pr-5 py-3 rounded-full shadow-2xl flex items-center gap-2 text-sm font-semibold transition-transform hover:-translate-y-0.5">
        <Download className="w-4 h-4" /> Install App
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Smartphone className="w-5 h-5 text-[#ea580c]" /> Install Shruti Tours App</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">Add Shruti Tours to your home screen for one-tap booking and offline access.</p>

          <div className="mt-2 rounded-xl border border-slate-200 overflow-hidden">
            <div className="flex text-xs font-semibold">
              <button onClick={()=>setPlatform("ios")} className={`flex-1 py-2 ${platform==="ios"?"bg-[#1e1b4b] text-white":"bg-slate-50 text-slate-600"}`} data-testid="pwa-tab-ios">iPhone / iPad</button>
              <button onClick={()=>setPlatform("android")} className={`flex-1 py-2 ${platform==="android"?"bg-[#1e1b4b] text-white":"bg-slate-50 text-slate-600"}`} data-testid="pwa-tab-android">Android</button>
              <button onClick={()=>setPlatform("desktop")} className={`flex-1 py-2 ${platform==="desktop"?"bg-[#1e1b4b] text-white":"bg-slate-50 text-slate-600"}`} data-testid="pwa-tab-desktop">Desktop</button>
            </div>
            <div className="p-4 text-sm space-y-3">
              {platform==="ios" && (
                <ol className="space-y-2">
                  <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#ea580c] text-white grid place-items-center text-xs font-bold flex-shrink-0">1</span><span>Open this website in <b>Safari</b> (not Chrome).</span></li>
                  <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#ea580c] text-white grid place-items-center text-xs font-bold flex-shrink-0">2</span><span>Tap the <Share className="w-4 h-4 inline text-blue-600" /> <b>Share</b> button at the bottom.</span></li>
                  <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#ea580c] text-white grid place-items-center text-xs font-bold flex-shrink-0">3</span><span>Scroll and tap <b>&quot;Add to Home Screen&quot;</b> <Plus className="w-4 h-4 inline" />.</span></li>
                  <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#ea580c] text-white grid place-items-center text-xs font-bold flex-shrink-0">4</span><span>Tap <b>Add</b> — the Shruti Tours icon appears on your home screen.</span></li>
                </ol>
              )}
              {platform==="android" && (
                <ol className="space-y-2">
                  <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#ea580c] text-white grid place-items-center text-xs font-bold flex-shrink-0">1</span><span>Open in <b>Chrome</b> browser.</span></li>
                  <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#ea580c] text-white grid place-items-center text-xs font-bold flex-shrink-0">2</span><span>Tap the <b>⋮ menu</b> (top right).</span></li>
                  <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#ea580c] text-white grid place-items-center text-xs font-bold flex-shrink-0">3</span><span>Tap <b>&quot;Install app&quot;</b> or <b>&quot;Add to Home screen&quot;</b>.</span></li>
                  <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#ea580c] text-white grid place-items-center text-xs font-bold flex-shrink-0">4</span><span>Tap <b>Install</b> — done!</span></li>
                </ol>
              )}
              {platform==="desktop" && (
                <ol className="space-y-2">
                  <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#ea580c] text-white grid place-items-center text-xs font-bold flex-shrink-0">1</span><span>Open in <b>Chrome / Edge</b>.</span></li>
                  <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#ea580c] text-white grid place-items-center text-xs font-bold flex-shrink-0">2</span><span>Look for the <Download className="w-4 h-4 inline" /> install icon in the address bar.</span></li>
                  <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#ea580c] text-white grid place-items-center text-xs font-bold flex-shrink-0">3</span><span>Click <b>Install</b> — the app opens in its own window.</span></li>
                </ol>
              )}
            </div>
          </div>

          <div className="flex gap-2 mt-2">
            <Button variant="outline" onClick={dismiss} className="flex-1" data-testid="pwa-dismiss">Don't show again</Button>
            {deferred && <Button onClick={install} className="flex-1 bg-[#ea580c] hover:bg-orange-700" data-testid="pwa-install-now">Install now <ChevronRight className="w-4 h-4 ml-1" /></Button>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
